import { LoaderFunction } from 'react-router-dom';
import debug from 'debug';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { selectActionById } from '../../../../../gql/selectors/actions';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import {
  reconcileValidationActionContext,
  releaseForeignValidationPointer,
} from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

// Loggers
const logLoader = debug('rois:actionsummary:loader');

const loadActionSummary = async (idOrder: string) => {
  // Habilitar logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:actionsummary:loader*');
  }

  logLoader('🚀 Cargando resumen de actuación para ID:', idOrder);

  try {
    // Intentamos obtener datos actualizados del servidor primero
    const serverData = await selectActionById(idOrder);
    logLoader('📊 Datos del servidor obtenidos:');
    logLoader(`  - Operadores: ${serverData?.operators?.length || 0}`);
    logLoader(`  - Vehículos: ${serverData?.vehicles?.length || 0}`);
    logLoader(`  - Partidas: ${serverData?.budgetItemsValue?.length || 0}`);
    logLoader(`  - Sketch: ${serverData?.sketch ? 'SÍ' : 'NO'}`);
    logLoader(`  - Imágenes: ${serverData?.images?.length || 0}`);
    logLoader(`  - isExternal: ${serverData?.isExternal}`);

    return serverData;
  } catch (err) {
    Sentry.captureException(err);
    logLoader('❌ Error cargando resumen de actuación:', err);
    return false;
  }
};

/**
 * ¿Es el resumen de VALIDACIÓN? Este loader sirve también los resúmenes de
 * CREACIÓN (`new-action/summary/:id`), y ahí las fotos son del borrador en
 * curso, no de la actuación: reclamarlas con su id tiraría la lista de fotos
 * del asistente al pulsar «Editar» (las reclama el asistente de creación).
 */
const isValidationSummaryRequest = (request: Request | undefined): boolean => {
  if (!request?.url) return false;
  try {
    return new URL(request.url).pathname.includes('/actions-validation/');
  } catch {
    return false;
  }
};

export const ActionSummaryLoader: LoaderFunction = async ({
  params,
  request,
}) => {
  // Síncrono y antes de cualquier `await` (diagnóstico 2026-10-08 § 8).
  if (isValidationSummaryRequest(request)) {
    // La URL manda: si el contexto de validación de la pestaña es de OTRA
    // actuación se borra entero, y con él las cachés de sus pestañas, antes
    // de que nadie las lea.
    reconcileValidationActionContext(params.id);
    // La lista de fotos y el croquis en curso también son de la actuación de
    // la URL. Las carpetas de OPFS no se tocan: las comparten las pestañas
    // (spec 2026-11-02 wizard-draft-ownership § 6).
    claimDraftScope(ACTION_MEDIA_SCOPE, params.id);
  } else {
    // Resumen de CREACIÓN: no es del asistente de validación. Solo se quita el
    // puntero de contexto si es de otra actuación; las copias de validación y
    // su dueño se quedan (Ruling 17): reclamarlas con el id de la recién
    // creada tiraba lo editado sin guardar de otra actuación.
    releaseForeignValidationPointer(params.id);
  }

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  console.log('📋 [ActionSummaryLoader] INICIO - params.id:', params.id);

  if (!params.id) {
    console.log('⚠️ [ActionSummaryLoader] No hay params.id');
    return null;
  }

  const loaderToResolve = await loadActionSummary(params.id);

  const sessionStorageRaw = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  console.log('🔍 [ActionSummaryLoader] SessionStorage raw:', {
    key: formActionNaming.NEW_VALIDATION_ACTION_DATA,
    hasData: !!sessionStorageRaw,
    length: sessionStorageRaw?.length || 0,
  });

  // Parsear sessionStorage y extraer SOLO la propiedad isActionEdited
  const parsedSessionData = sessionStorageRaw
    ? JSON.parse(decompressSessionStorage(sessionStorageRaw))
    : null;

  // BUG #11 FIX: Fallback pattern cuando sessionStorage está vacío pero params.id existe
  // Esto ocurre después de logout/login o navegación directa por URL
  let isActionEdited: string;

  if (parsedSessionData?.isActionEdited) {
    // Caso normal: sessionStorage tiene datos
    isActionEdited = parsedSessionData.isActionEdited;
  } else if (params.id) {
    // Fallback: sessionStorage vacío pero navegando a una acción específica
    // En flujo de validación sin sessionStorage = modo vista (no edición)
    isActionEdited = 'false';
  } else {
    // Default: modo edición (crear nueva acción)
    isActionEdited = 'true';
  }

  console.log(
    '🚀 [ActionSummaryLoader] loaderToResolve:',
    JSON.stringify(loaderToResolve)
  );
  console.log(
    '🚀 [ActionSummaryLoader] parsedSessionData:',
    JSON.stringify(parsedSessionData)
  );
  console.log(
    '🚀 [ActionSummaryLoader] isActionEdited (extracted):',
    isActionEdited
  );

  const data = { isActionEdited, ...loaderToResolve };
  console.log('🚀 [ActionSummaryLoader] Data final:', JSON.stringify(data));
  console.log('✅ [ActionSummaryLoader] COMPLETADO');

  return data;
};

  return { data: load() };
};
